import React, { useEffect, useState } from "react";
import axios from "axios";
import { connect } from "react-redux";
import HeaderTablePage from "../components/HeaderTablePage";
import LoadingTable from "../components/LoadingTable";
import appConfig from "../../config/appConfig";
import { ToastBottomEnd } from "../components/Toast";
import {
  toastEditErrorMessageConfig,
  toastEditSuccessMessageConfig,
} from "../../config/toastConfig";
import { verifyPermission } from "../../helpers/permission";
import { ERROR_401 } from "../../config/errorPage";
import { verifyTokenExpire } from "../../middleware/verifyToken";
import { useTranslation } from "react-i18next";

const formatTimeToHourMinute = (value) => {
  if (!value) return "";
  const str = String(value);
  const parts = str.split(":");
  if (!parts[0] || !parts[1]) return "";
  const hh = parts[0].padStart(2, "0");
  const mm = parts[1].padStart(2, "0");
  return `${hh}:${mm}`;
};

const ChatbotOfficeHours = (props) => {
  const { t, ready } = useTranslation();

  // if (!verifyPermission(props.userPermissions, "history-list-receipt")) {
  //   window.location.href = ERROR_401;
  // }

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [entries, setEntries] = useState([]);
  const [newEntry, setNewEntry] = useState({
    day: "",
    start_hour: "",
    end_hour: "",
  });

  const days = [
    { key: "monday", label: t("Lundi") },
    { key: "tuesday", label: t("Mardi") },
    { key: "wednesday", label: t("Mercredi") },
    { key: "thursday", label: t("Jeudi") },
    { key: "friday", label: t("Vendredi") },
    { key: "saturday", label: t("Samedi") },
    { key: "sunday", label: t("Dimanche") },
  ];

  const institutionId = props.user?.institution?.id;

  useEffect(() => {
    const fetchOfficeHours = async () => {
      if (!verifyTokenExpire() || !institutionId) {
        setLoading(false);
        return;
      }

      try {
        const { data } = await axios.get(
          `${appConfig.apiDomaine}/chatbot-office-hours/${institutionId}`
        );

        if (Array.isArray(data)) {
          setEntries(
            data.map((item) => ({
              id: item.id,
              day: item.day,
              start_hour: formatTimeToHourMinute(item.start_hour),
              end_hour: formatTimeToHourMinute(item.end_hour),
            }))
          );
        } else {
          setEntries([]);
        }
      } catch (error) {
        // In case of error, keep default empty configuration
        console.error("Erreur lors du chargement des horaires du chatbot", error);
      } finally {
        setLoading(false);
      }
    };

    fetchOfficeHours();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [institutionId]);

  const handleNewEntryChange = (field, value) => {
    setNewEntry((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddEntry = () => {
    if (!newEntry.day || !newEntry.start_hour || !newEntry.end_hour) {
      ToastBottomEnd.fire(toastEditErrorMessageConfig());
      return;
    }

    setEntries((prev) => [
      ...prev,
      {
        day: newEntry.day,
        start_hour: newEntry.start_hour,
        end_hour: newEntry.end_hour,
      },
    ]);

    setNewEntry({
      day: "",
      start_hour: "",
      end_hour: "",
    });
  };

  const handleDeleteEntry = async (index) => {
    const entry = entries[index];

    // Suppression locale pour les entrées non encore enregistrées
    if (!entry || !entry.id) {
      setEntries((prev) => prev.filter((_, i) => i !== index));
      return;
    }

    if (!verifyTokenExpire() || !institutionId) return;

    try {
      await axios.delete(
        `${appConfig.apiDomaine}/chatbot-office-hours/${entry.id}`
      );
      setEntries((prev) => prev.filter((_, i) => i !== index));
      ToastBottomEnd.fire(toastEditSuccessMessageConfig());
    } catch (error) {
      console.error("Erreur lors de la suppression de l'horaire du chatbot", error);
      ToastBottomEnd.fire(toastEditErrorMessageConfig());
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!verifyTokenExpire() || !institutionId) return;

    if (!entries.length) {
      ToastBottomEnd.fire(toastEditErrorMessageConfig());
      return;
    }

    const payload = entries.map((e) => ({
      institution_id: institutionId,
      day: e.day,
      start_hour: formatTimeToHourMinute(e.start_hour),
      end_hour: formatTimeToHourMinute(e.end_hour),
    }));

    setSaving(true);
    try {
      await axios.post(
        `${appConfig.apiDomaine}/chatbot-office-hours`,
        payload
      );
      ToastBottomEnd.fire(toastEditSuccessMessageConfig());
    } catch (error) {
      console.error("Erreur lors de la sauvegarde des horaires du chatbot", error);
      ToastBottomEnd.fire(toastEditErrorMessageConfig());
    } finally {
      setSaving(false);
    }
  };

  return ready ? (
    <div
      className="kt-content  kt-grid__item kt-grid__item--fluid kt-grid kt-grid--hor"
      id="kt_content"
    >
      <div className="kt-subheader   kt-grid__item" id="kt_subheader">
        <div className="kt-container  kt-container--fluid ">
          <div className="kt-subheader__main">
            <h3 className="kt-subheader__title">
              {t("Horaires du chatbot")}
            </h3>
            <span className="kt-subheader__separator kt-hidden" />
            <div className="kt-subheader__breadcrumbs">
              <a href="#icone" className="kt-subheader__breadcrumbs-home">
                <i className="flaticon2-shelter" />
              </a>
              <span className="kt-subheader__breadcrumbs-separator" />
              <a
                href="#button"
                onClick={(e) => e.preventDefault()}
                className="kt-subheader__breadcrumbs-link"
                style={{ cursor: "text" }}
              >
                {t("Configuration des horaires du chatbot")}
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="kt-container  kt-container--fluid  kt-grid__item kt-grid__item--fluid">
        <div className="kt-portlet">
          <HeaderTablePage title={t("Horaires du chatbot")} />

          {loading ? (
            <LoadingTable />
          ) : (
            <div className="kt-portlet__body">
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <div className="form-row align-items-end">
                    <div className="form-group col-md-4">
                      <label>{t("Jour")}</label>
                      <select
                        className="form-control"
                        value={newEntry.day}
                        onChange={(e) =>
                          handleNewEntryChange("day", e.target.value)
                        }
                      >
                        <option value="">{t("Sélectionner un jour")}</option>
                        {days.map((d) => (
                          <option key={d.key} value={d.key}>
                            {d.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group col-md-3">
                      <label>{t("Heure de début")}</label>
                      <input
                        type="time"
                        className="form-control"
                        value={newEntry.start_hour}
                        onChange={(e) =>
                          handleNewEntryChange("start_hour", e.target.value)
                        }
                      />
                    </div>
                    <div className="form-group col-md-3">
                      <label>{t("Heure de fin")}</label>
                      <input
                        type="time"
                        className="form-control"
                        value={newEntry.end_hour}
                        onChange={(e) =>
                          handleNewEntryChange("end_hour", e.target.value)
                        }
                      />
                    </div>
                    <div className="form-group col-md-2 text-right">
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleAddEntry}
                      >
                        {t("Ajouter")}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="table-responsive">
                  <table className="table table-striped table-bordered">
                    <thead>
                      <tr>
                        <th>{t("Jour")}</th>
                        <th>{t("Heure de début")}</th>
                        <th>{t("Heure de fin")}</th>
                        <th style={{ width: "80px" }}>{t("Action")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entries.length ? (
                        entries.map((entry, index) => {
                          const dayLabel =
                            days.find((d) => d.key === entry.day)?.label ||
                            entry.day;
                          return (
                            <tr key={index}>
                              <td>{dayLabel}</td>
                              <td>{entry.start_hour}</td>
                              <td>{entry.end_hour}</td>
                              <td className="text-center">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-danger"
                                  onClick={() => handleDeleteEntry(index)}
                                >
                                  {t("Supprimer")}
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="4" className="text-center">
                            {t("Aucune entrée d'horaire pour le moment")}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="kt-portlet__foot">
                  <div className="kt-form__actions text-right">
                    {saving ? (
                      <button
                        className="btn btn-primary kt-spinner kt-spinner--left kt-spinner--md kt-spinner--light"
                        type="button"
                        disabled
                      >
                        {t("Sauvegarde")}...
                      </button>
                    ) : (
                      <button type="submit" className="btn btn-primary">
                        {t("Enregistrer")}
                      </button>
                    )}
                  </div>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  ) : null;
};

const mapStateToProps = (state) => {
  return {
    userPermissions: state.user.user.permissions,
    user: state.user.user,
  };
};

export default connect(mapStateToProps)(ChatbotOfficeHours);

